import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { Field } from "@/components/Form";
import { Eyebrow, Headline } from "@/components/Editorial";
import PortalToggle from "@/components/PortalToggle";
import { useAuth } from "@/lib/auth";
import { clearResearchEntrySignal } from "@/lib/researchAccess";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

// Allowlist of internal paths that the `next` query param can redirect
// to after sign-in. Anything else (open redirect risk, external URLs)
// falls back to `/catalogue` (the default post-sign-in landing since
// the Feb 29 pivot — consumers land on the catalogue, not the old
// member portal). Keep this short and add entries deliberately.
const SAFE_NEXT_PATHS = new Set(["/research", "/catalogue", "/app", "/app/plan", "/app/me"]);

function safeNextFrom(searchParams) {
  const raw = searchParams.get("next") || "";
  if (!raw.startsWith("/")) return null;
  // Strip query/hash so the allowlist match is exact on path.
  const path = raw.split("?")[0].split("#")[0];
  return SAFE_NEXT_PATHS.has(path) ? raw : null;
}

export default function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNextFrom(searchParams);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null); // {status, message}

  const submit = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      toast("Please enter your email and password.", "error");
      return;
    }
    setAuthError(null);
    setLoading(true);
    try {
      await login(email, password);
      // Sign-in consumes the research entry signal: by this point the
      // returned consumer object already carries the authoritative
      // researchAccess flag (existing accounts only get it via the
      // admin toggle). The signal has served its purpose at sign-up
      // time only — clear it so a subsequent unrelated sign-up on
      // this same tab session doesn't inherit it by accident.
      clearResearchEntrySignal();
      // Catalogue-first pivot (Feb 29): consumer members land on the
      // catalogue by default. `next=` still overrides for deep-link
      // returns (e.g. `/sign-in?next=/app/plan` from a plan reminder).
      navigate(nextPath || "/catalogue");
    } catch (err) {
      const message = err.detail || "We couldn't sign you in with those details. Try again, or reset your password.";
      setAuthError({ status: err.status || 0, message });
      // Clear the password so a brute-force fat-finger doesn't lock
      // them out — they can retype without nuking the email.
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  const useDifferentEmail = () => {
    setAuthError(null);
    setEmail("");
    setPassword("");
    // Focus is best-effort — the Field component owns its input ref so
    // we let the user tap it. Toast is gentler than yanking focus.
  };

  return (
    <div data-testid="signin-page" style={{ minHeight: "100vh", background: C.cream, display: "flex", flexDirection: "column" }}>
      <header style={{ background: C.ink, padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" data-testid="signin-logo-link"><Logo light size={22} /></Link>
        <Link to="/" data-testid="signin-close" style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: "rgba(250,246,240,0.6)", textDecoration: "none" }}>
          CLOSE ✕
        </Link>
      </header>

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <form onSubmit={submit} data-testid="signin-form" method="post" action="/api/consumer/auth/login" style={{ width: "100%", maxWidth: 460, background: C.pure, padding: "48px 36px", border: `1.5px solid ${C.warmGray3}` }}>
          <Eyebrow testId="signin-eyebrow">★ WELCOME BACK</Eyebrow>
          <Headline before="SIGN" italic="in." size={64} className="mt-4" />

          <div style={{ marginTop: 22 }}>
            <PortalToggle active="member" />
          </div>

          <Field label="EMAIL" type="email" name="email" id="signin-email" autoComplete="username webauthn" value={email} onChange={setEmail} placeholder="you@email.com" testId="signin-email-input" autoFocus />
          <Field label="PASSWORD" type="password" name="password" id="signin-password" autoComplete="current-password webauthn" value={password} onChange={setPassword} placeholder="••••••••" testId="signin-password-input" />

          {authError && (
            <div
              data-testid="signin-error-band"
              role="alert"
              style={{
                marginTop: 18,
                padding: "14px 16px",
                background: C.peach,
                color: C.ink,
                border: `1px solid ${C.peachDeep}`,
                fontSize: 13,
                lineHeight: 1.55,
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.2em", color: C.peachDeep, marginBottom: 6 }}>
                ★ COULDN'T SIGN YOU IN
              </div>
              <div style={{ marginBottom: 12 }}>{authError.message}</div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em" }}>
                <Link
                  to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ""}`}
                  data-testid="signin-error-reset-link"
                  style={{ color: C.ink, textDecoration: "underline", textUnderlineOffset: 3 }}
                >
                  RESET PASSWORD →
                </Link>
                <button
                  type="button"
                  onClick={useDifferentEmail}
                  data-testid="signin-error-different-email"
                  style={{
                    background: "transparent",
                    border: 0,
                    padding: 0,
                    color: C.peachDeep,
                    textDecoration: "underline",
                    textUnderlineOffset: 3,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.18em",
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  USE A DIFFERENT EMAIL →
                </button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 28 }}>
            <Button type="submit" variant="primary" block testId="signin-submit-btn" disabled={loading}>
              {loading ? "SIGNING IN…" : "SIGN IN →"}
            </Button>
          </div>

          <div style={{ marginTop: 22, textAlign: "center" }}>
            <Link to="/forgot-password" data-testid="forgot-password-link" style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontSize: 16, color: C.warmGray, textDecoration: "underline" }}>
              Forgot password?
            </Link>
          </div>

          <div style={{ marginTop: 36, paddingTop: 24, borderTop: `1px solid ${C.warmGray4}`, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: C.warmGray, marginBottom: 12 }}>Not a member yet?</div>
            <Link to={`/get-access${nextPath ? `?next=${encodeURIComponent(nextPath)}` : ""}`} data-testid="signin-getaccess-link" style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.18em", color: C.ink, textDecoration: "none" }}>
              REQUEST ACCESS →
            </Link>
          </div>

          <div style={{ marginTop: 22, textAlign: "center", fontSize: 12, color: C.warmGray }}>
            Affiliate?{" "}
            <Link
              to="/affiliates/sign-in"
              data-testid="signin-affiliate-link"
              style={{
                fontFamily: '"Instrument Serif", serif',
                fontStyle: "italic",
                fontSize: 14,
                color: C.peachDeep,
                textDecoration: "underline",
                textUnderlineOffset: 3,
                marginLeft: 4,
              }}
            >
              Sign in to the affiliate portal →
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
